import React, { useState, useEffect } from 'react';
import {
  PlanNotFoundError,
  getPlan,
  getSession,
  exportPlan,
  downloadBusinessPlanDocx,
  downloadBusinessPlanPdf,
  downloadSbaPackage,
} from '../services/api';
import { getVerticalConfig, isRestaurantOnly } from '../config/verticals';

const SOURCE_LABELS = {
  user_provided: 'Your input',
  rag_benchmark: 'Industry benchmark',
  system_default: 'System default',
};

function fmt(value) {
  if (value == null) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// Key-metric formatter keyed by the config's `format` hint. `percent` treats
// the value as a 0–1 fraction (utilization), `bigpercent` as already 0–100
// (break-even occupancy).
function formatMetric(fmtHint, value) {
  if (value == null) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : Number(value);
  switch (fmtHint) {
    case 'currency':   return fmt(num);
    case 'integer':    return isNaN(num) ? String(value) : Math.round(num).toLocaleString('en-US');
    case 'percent':    return isNaN(num) ? String(value) : `${(num * 100).toFixed(1)}%`;
    case 'bigpercent': return isNaN(num) ? String(value) : `${num.toFixed(1)}%`;
    default:           return String(value);
  }
}

// Resolve a keyMetric to its display value from the right source.
function metricValue(metric, plan, session) {
  let raw;
  if (metric.source === 'breakeven') raw = plan.break_even?.[metric.key];
  else if (metric.source === 'collected') raw = session?.collected_inputs?.[metric.key]?.value;
  if (raw == null) return null;
  return formatMetric(metric.format, raw);
}

export default function PlanView({ sessionId, onBack }) {
  const [plan, setPlan] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  // Business-plan / SBA download in flight: 'docx' | 'pdf' | 'zip' | null.
  const [downloading, setDownloading] = useState(null);
  // 'summary' | 'detailed' — preserved across tab switches so the
  // user doesn't lose either view. Plan + session data are loaded
  // once on mount and shared between both tabs.
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (!sessionId) return;
    setLoading(true);
    Promise.all([getPlan(sessionId), getSession(sessionId)])
      .then(([p, s]) => {
        setPlan(p);
        setSession(s);
      })
      .catch((err) => setError(
        err instanceof PlanNotFoundError
          ? 'Your financial plan needs to be recalculated — your answers have '
            + 'changed since it was last generated. Head back to the chat and '
            + 'ask Eliza to run the numbers again.'
          : err.message
      ))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const handleExport = async (format) => {
    setExporting(format);
    try {
      await exportPlan(sessionId, format);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  // Business-plan + SBA-package downloads. Gated on bp_status === 'complete'.
  const handleDownload = async (kind) => {
    setDownloading(kind);
    try {
      if (kind === 'docx') await downloadBusinessPlanDocx(sessionId);
      else if (kind === 'pdf') await downloadBusinessPlanPdf(sessionId);
      else if (kind === 'zip') await downloadSbaPackage(sessionId);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return <div className="plan-view"><p className="plan-loading">Loading your financial plan...</p></div>;
  }

  if (error) {
    return (
      <div className="plan-view">
        <div className="plan-error">{error}</div>
        <button className="plan-back-btn" onClick={onBack}>Back to Chat</button>
      </div>
    );
  }

  if (!plan) return null;

  const base = plan.scenarios?.scenarios?.find((s) => s.scenario_name === 'base_case');

  return (
    <div className="plan-view">
      <div className="plan-header">
        <button className="plan-back-btn" onClick={onBack}>Back to Chat</button>
        <h1>Financial Plan</h1>
        <div className="plan-export-btns">
          <div className="plan-export-group">
            <span className="plan-export-label">Financials</span>
            <button onClick={() => handleExport('excel')} disabled={!!exporting}>
              {exporting === 'excel' ? 'Exporting...' : 'Excel'}
            </button>
            <button onClick={() => handleExport('csv')} disabled={!!exporting}>
              {exporting === 'csv' ? 'Exporting...' : 'CSV'}
            </button>
          </div>

          {(() => {
            const bpComplete = session?.bp_status === 'complete';
            const lockTip = bpComplete ? undefined : 'Complete the business plan first';
            return (
              <>
                <div className="plan-export-group">
                  <span className="plan-export-label">Business Plan</span>
                  <button
                    disabled={!bpComplete || !!downloading}
                    title={lockTip}
                    onClick={() => handleDownload('docx')}
                  >
                    {downloading === 'docx' ? 'Preparing...' : 'Word (.docx)'}
                  </button>
                  <button
                    disabled={!bpComplete || !!downloading}
                    title={lockTip}
                    onClick={() => handleDownload('pdf')}
                  >
                    {downloading === 'pdf' ? 'Preparing...' : 'PDF'}
                  </button>
                </div>

                <div className="plan-export-group">
                  <span className="plan-export-label">SBA Package</span>
                  <button
                    disabled={!bpComplete || !!downloading}
                    title={lockTip}
                    onClick={() => handleDownload('zip')}
                  >
                    {downloading === 'zip' ? 'Assembling...' : 'ZIP'}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* Tab strip — Summary (default) vs Detailed Model. State lives
          on PlanView, so switching back and forth never loses either
          view's scroll position or any pending interaction. */}
      <div className="plan-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'summary'}
          className={`plan-tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'detailed'}
          className={`plan-tab ${activeTab === 'detailed' ? 'active' : ''}`}
          onClick={() => setActiveTab('detailed')}
        >
          Detailed Model
        </button>
      </div>

      {activeTab === 'detailed'
        ? renderDetailed(plan)
        : renderSummary(plan, session, base, session?.vertical || 'restaurant')}
    </div>
  );
}

// ─── Summary view ─────────────────────────────────────────────────────────
// Pulled into a function so the JSX-vs-tabs branching stays readable.
// Receives the same data the component already loaded.

function renderSummary(plan, session, base, vertical) {
  const config = getVerticalConfig(vertical);

  // Startup line items come from config; 'plan'-sourced rows read the engine
  // output, 'collected'-sourced rows read the raw capital inputs (services and
  // retail itemise their startup from collected_inputs because the engine
  // lumps them into the restaurant-shaped startup_costs).
  const startupRows = config.startupLineItems.map((item) => {
    const value =
      item.source === 'collected'
        ? session?.collected_inputs?.[item.key]?.value
        : plan.startup_costs?.[item.key];
    return (
      <tr key={item.key} className={item.rowClass || ''}>
        <td>{item.label}</td>
        <td>{fmt(value)}</td>
      </tr>
    );
  });

  // Key metric cards: vertical-varying metrics from config + a common
  // First-Profitable-Month card.
  const metricCards = config.keyMetrics
    .filter((m) => !(isRestaurantOnly(m.key) && vertical !== 'restaurant'))
    .map((m) => {
      const value = metricValue(m, plan, session);
      if (value == null) return null;
      return (
        <div key={m.key} className="metric-card">
          <span className="metric-label">{m.label}</span>
          <span className="metric-value">{value}</span>
        </div>
      );
    })
    .filter(Boolean);

  return (
    <>
      {/* Section 1 — Startup Summary */}
      <section className="plan-section">
        <h2>Startup Summary</h2>
        <div className="plan-hero-metric">
          <span className="hero-label">Total Capital Required</span>
          <span className="hero-value">{fmt(plan.startup_costs?.total_capital_required)}</span>
        </div>
        <table className="plan-table">
          <thead>
            <tr><th>Item</th><th>Amount</th></tr>
          </thead>
          <tbody>{startupRows}</tbody>
        </table>
      </section>

      {/* Section 2 — Key Metrics */}
      <section className="plan-section">
        <h2>Key Metrics</h2>
        <div className="metrics-grid">
          {metricCards}
          <div className="metric-card">
            <span className="metric-label">First Profitable Month</span>
            <span className="metric-value">
              {base?.first_profitable_month ? `Month ${base.first_profitable_month}` : 'N/A'}
            </span>
          </div>
        </div>
      </section>

      {/* Section 3 — Scenario Comparison */}
      <section className="plan-section">
        <h2>Scenario Comparison</h2>
        <table className="plan-table scenario-table">
          <thead>
            <tr>
              <th>Metric</th>
              {plan.scenarios?.scenarios?.map((s) => (
                <th key={s.scenario_name}>{s.scenario_name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>First Profitable Month</td>
              {plan.scenarios?.scenarios?.map((s) => (
                <td key={s.scenario_name}>
                  {s.first_profitable_month ? `Month ${s.first_profitable_month}` : 'N/A'}
                </td>
              ))}
            </tr>
            <tr>
              <td>36-Month Net Income</td>
              {plan.scenarios?.scenarios?.map((s) => (
                <td key={s.scenario_name}>{fmt(s.cumulative_net_income_36)}</td>
              ))}
            </tr>
            <tr>
              <td>36-Month Revenue</td>
              {plan.scenarios?.scenarios?.map((s) => (
                <td key={s.scenario_name}>{fmt(s.total_revenue_36)}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </section>

      {/* Section 4 — Assumption Log */}
      {session?.collected_inputs && Object.keys(session.collected_inputs).length > 0 && (
        <section className="plan-section">
          <h2>Assumption Log</h2>
          <table className="plan-table assumption-table">
            <thead>
              <tr><th>Field</th><th>Value</th><th>Source</th></tr>
            </thead>
            <tbody>
              {Object.values(session.collected_inputs).map((input) => (
                <tr key={input.field_name}>
                  <td>{input.field_name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                  <td>{String(input.value)}</td>
                  <td>
                    <span className={`source-tag ${input.source_tag}`}>
                      {SOURCE_LABELS[input.source_tag] || input.source_tag}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </>
  );
}

// ─── Detailed Model view ──────────────────────────────────────────────────
// Month-by-month P&L for the base case. Cash-flow closing balance is
// shown in the right-hand column so the user can see the runway as
// the months tick by. Heavy data — for the full 36-month grid across
// every sheet, the Excel export is the canonical view.

function renderDetailed(plan) {
  const months = plan.pnl?.months || [];
  const cfMonths = plan.cash_flow?.months || [];
  // Cash-flow months include month 0 (pre-opening); P&L starts at
  // month 1. Map by month number so the join is unambiguous.
  const cfByMonth = Object.fromEntries(cfMonths.map((m) => [m.month, m]));

  return (
    <section className="plan-section">
      <h2>36-Month Profit &amp; Loss (Base Case)</h2>
      <p className="plan-section-note">
        Per-month detail. For the full balance sheet, scenarios, and
        cash-flow ledger, download the Excel export above — every
        sheet is included.
      </p>
      <div className="plan-table-scroll">
        <table className="plan-table detailed-pnl">
          <thead>
            <tr>
              <th>Month</th>
              <th>Revenue</th>
              <th>COGS</th>
              <th>Gross Profit</th>
              <th>Operating Expenses</th>
              <th>Net Income</th>
              <th>Closing Cash</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => {
              const cf = cfByMonth[m.month];
              const negativeCash = cf && parseFloat(cf.closing_balance) < 0;
              return (
                <tr key={m.month}>
                  <td>Month {m.month}</td>
                  <td>{fmt(m.revenue)}</td>
                  <td>{fmt(m.cogs)}</td>
                  <td>{fmt(m.gross_profit)}</td>
                  <td>{fmt(m.total_fixed_costs)}</td>
                  <td className={parseFloat(m.net_income) < 0 ? 'negative' : ''}>
                    {fmt(m.net_income)}
                  </td>
                  <td className={negativeCash ? 'negative' : ''}>
                    {cf ? fmt(cf.closing_balance) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
